import React, { useEffect } from 'react'
import {
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Button,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material'
import { Save, Visibility, VisibilityOff } from '@mui/icons-material'
import { useCertificateContext, usePinContext } from '../../providers'

export const SimplifiedCertificateSelection: React.FC = () => {
  const {
    selectedCertificate,
    handleSelectedCertificate,
    allCertificates,
    loadAllCertificates,
    certificateLoading,
    certificateError,
  } = useCertificateContext()
  const { pinCode, setPinCode, savePinCode, showPinCode, setShowPinCode } =
    usePinContext()

  useEffect(() => {
    loadAllCertificates()
  }, [])

  const handleCertificateChange = (value: string) => {
    if (!value) {
      handleSelectedCertificate(null)
      return
    }

    const found = allCertificates.find(
      (item) => item.cert.serialNumber === value,
    )

    if (found) {
      handleSelectedCertificate({
        cert: found.cert,
        terminal: found.terminal,
        client: found.client,
        pinFromClient: found.cert.pinFromClient,
      })
    }
  }

  if (certificateLoading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="center" p={3}>
            <CircularProgress sx={{ mr: 2 }} />
            <Typography>Sertifikalar yükleniyor...</Typography>
          </Box>
        </CardContent>
      </Card>
    )
  }

  if (certificateError) {
    return (
      <Card>
        <CardContent>
          <Alert
            severity="error"
            action={
              <Typography
                variant="body2"
                sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                onClick={loadAllCertificates}
              >
                Tekrar Dene
              </Typography>
            }
          >
            {certificateError}
          </Alert>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" gutterBottom>
            Sertifika Seçimi
          </Typography>

          <Button
            onClick={async () => {
              handleSelectedCertificate(null)
              await loadAllCertificates()
            }}
          >
            Yeniden Yükle
          </Button>
        </Box>

        {allCertificates.length > 0 ? (
          <Box>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Sertifika Seçin</InputLabel>
              <Select
                value={selectedCertificate?.cert?.serialNumber ?? ''}
                label="Sertifika Seçin"
                onChange={(e) => handleCertificateChange(e.target.value)}
              >
                <MenuItem value="">Sertifika Seçin</MenuItem>
                {allCertificates.map((item, index) => (
                  <MenuItem key={index} value={item.cert.serialNumber}>
                    {item.displayName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedCertificate && (
              <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Seçili Sertifika Detayları:
                </Typography>
                <Typography variant="body2">
                  <strong>İsim:</strong>{' '}
                  {selectedCertificate.cert.subjectCommonName}
                </Typography>
                <Typography variant="body2">
                  <strong>Seri No:</strong>{' '}
                  {selectedCertificate.cert.serialNumber}
                </Typography>
                <Typography variant="body2">
                  <strong>Geçerlilik:</strong>{' '}
                  {selectedCertificate.cert.notBefore} -{' '}
                  {selectedCertificate.cert.notAfter}
                </Typography>
                <Typography variant="body2">
                  <strong>Client:</strong> {selectedCertificate.client.name}
                </Typography>
                <Typography variant="body2">
                  <strong>Terminal:</strong>{' '}
                  {selectedCertificate.terminal.library} - Slot:{' '}
                  {selectedCertificate.terminal.slotId}
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip
                    label={
                      selectedCertificate.cert.isExpired === 'true'
                        ? 'Süresi Geçmiş'
                        : 'Geçerli'
                    }
                    color={
                      selectedCertificate.cert.isExpired === 'true'
                        ? 'error'
                        : 'success'
                    }
                    size="small"
                    sx={{ mr: 1 }}
                  />
                  <Chip
                    label={
                      selectedCertificate.client.connected
                        ? 'Client Bağlı'
                        : 'Client Bağlı Değil'
                    }
                    color={
                      selectedCertificate.client.connected ? 'success' : 'error'
                    }
                    size="small"
                  />
                </Box>
                <Box sx={{ mt: 2, width: '100%' }}>
                  <TextField
                    label="PIN Kodu"
                    value={pinCode}
                    onChange={(e) => setPinCode(e.target.value)}
                    fullWidth
                    type={showPinCode ? 'text' : 'password'}
                    disabled={selectedCertificate.pinFromClient}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <>
                            <IconButton
                              onClick={() =>
                                savePinCode(
                                  selectedCertificate.cert.serialNumber,
                                  pinCode,
                                )
                              }
                              disabled={selectedCertificate.pinFromClient}
                            >
                              <Save />
                            </IconButton>
                            <IconButton
                              onClick={() => setShowPinCode(!showPinCode)}
                              edge="end"
                              disabled={selectedCertificate.pinFromClient}
                            >
                              {showPinCode ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Box>
            )}
          </Box>
        ) : (
          <Alert severity="info">
            Hiç sertifika bulunamadı. Clientların bağlı olduğundan emin olun.
          </Alert>
        )}
      </CardContent>
    </Card>
  )
}
